import { useState, useEffect, useCallback } from "react";
import { PlusCircle, Trash01 } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { NativeSelect } from "@/components/base/select";
import { TextArea } from "@/components/base/textarea";
import { Button } from "@/components/base/buttons/button";
import { getCustomers, getCustomer, type CustomerWithVehicles } from "@/api/customers";
import type { CustomerDto, JobDto } from "@/types/dto";

interface LineItem {
  description: string;
  quantity: string;
  unitPrice: string;
}

interface JobFormValues {
  customerId: string | null;
  vehicleId: string | null;
  description: string | null;
  status: string;
  estimatedCompletion: string | null;
  notes: string | null;
  lines: LineItem[];
}

interface JobFormProps {
  defaultValues?: Partial<JobDto & { lines?: LineItem[] }>;
  onSubmit: (values: JobFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "In Progress", value: "in_progress" },
  { label: "Awaiting Parts", value: "awaiting_parts" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

export default function JobForm({ defaultValues, onSubmit, onCancel, isLoading }: JobFormProps) {
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithVehicles | null>(null);

  const [customerId, setCustomerId] = useState(defaultValues?.customerId ?? "");
  const [vehicleId, setVehicleId] = useState(defaultValues?.vehicleId ?? "");
  const [status, setStatus] = useState(defaultValues?.status ?? "pending");
  const [estimatedCompletion, setEstimatedCompletion] = useState(defaultValues?.estimatedCompletion ?? "");
  const [notes, setNotes] = useState(defaultValues?.notes ?? "");
  const [lines, setLines] = useState<LineItem[]>(
    defaultValues?.lines?.length ? defaultValues.lines : [{ description: "", quantity: "1", unitPrice: "" }]
  );

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await getCustomers({ limit: 100 });
      setCustomers(data.data);
    } catch {
      // handled
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (!customerId) {
      setSelectedCustomer(null);
      setVehicleId("");
      return;
    }
    (async () => {
      try {
        const data = await getCustomer(customerId);
        setSelectedCustomer(data);
        if (!data.vehicles.find((v) => v.id === vehicleId)) {
          setVehicleId("");
        }
      } catch {
        setSelectedCustomer(null);
      }
    })();
  }, [customerId]);

  const customerOptions = [
    { label: "Select customer...", value: "" },
    ...customers.map((c) => ({ label: `${c.firstName} ${c.lastName}`, value: c.id })),
  ];

  const vehicleOptions = [
    { label: "Select vehicle...", value: "" },
    ...(selectedCustomer?.vehicles ?? []).map((v) => ({
      label: `${v.make} ${v.model} (${v.year}) — ${v.licensePlate}`,
      value: v.id,
    })),
  ];

  const updateLine = (index: number, field: keyof LineItem, value: string) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, { description: "", quantity: "1", unitPrice: "" }]);

  const removeLine = (index: number) => {
    if (lines.length > 1) setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const total = lines.reduce((sum, l) => {
    return sum + (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0);
  }, 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const description = lines
      .filter((l) => l.description.trim())
      .map((l) => {
        const qty = parseInt(l.quantity, 10) || 1;
        const price = parseFloat(l.unitPrice) || 0;
        return `${l.description} (x${qty} @ ${formatCurrency(price)})`;
      })
      .join("\n");

    onSubmit({
      customerId: customerId || null,
      vehicleId: vehicleId || null,
      description: description || null,
      status,
      estimatedCompletion: estimatedCompletion || null,
      notes: notes || null,
      lines,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NativeSelect
          label="Customer"
          options={customerOptions}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
        />
        <NativeSelect
          label="Vehicle"
          options={vehicleOptions}
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          disabled={!selectedCustomer}
        />
      </div>

      {/* Line Items */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-medium text-text-primary">Work Items</p>
          <button type="button" onClick={addLine} className="text-fg-brand-primary hover:text-fg-brand-primary_hover transition-colors">
            <PlusCircle className="size-4.5" />
          </button>
        </div>
        <div className="space-y-2">
          {lines.map((line, index) => (
            <div key={index} className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  placeholder="Description"
                  size="sm"
                  value={line.description}
                  onChange={(v: string) => updateLine(index, "description", v)}
                />
              </div>
              <div className="w-16">
                <Input
                  placeholder="Qty"
                  type="number"
                  size="sm"
                  value={line.quantity}
                  onChange={(v: string) => updateLine(index, "quantity", v)}
                />
              </div>
              <div className="w-24">
                <Input
                  placeholder="Price"
                  type="number"
                  size="sm"
                  value={line.unitPrice}
                  onChange={(v: string) => updateLine(index, "unitPrice", v)}
                />
              </div>
              <div className="w-20 text-right pt-2">
                <span className="text-sm text-text-primary">
                  {formatCurrency((parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0))}
                </span>
              </div>
              {lines.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  color="link-destructive"
                  iconLeading={Trash01}
                  onClick={() => removeLine(index)}
                  aria-label="Remove"
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-2">
          <span className="text-sm font-semibold text-text-primary">Total: {formatCurrency(total)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NativeSelect
          label="Status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
        />
        <Input
          label="Estimated Completion"
          type="date"
          value={estimatedCompletion}
          onChange={setEstimatedCompletion}
        />
      </div>

      <TextArea
        label="Notes"
        placeholder="Internal notes..."
        rows={2}
        value={notes}
        onChange={setNotes}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" size="md" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="md" color="primary" isLoading={isLoading} isDisabled={isLoading}>
          {defaultValues?.id ? "Save" : "Create Job"}
        </Button>
      </div>
    </form>
  );
}
